/**
 * Filling in a questionnaire, and reading the result once it opens.
 *
 * The result screen has no charts on purpose: a big number and a sentence that
 * means something, per dimension. The sentence is written by hand, not
 * generated — it is the whole value of the exercise.
 */

import { apiGet, apiPost, ApiError } from '../js/api.js'
import { icon } from '../js/icons.js'
import { text } from '../js/html.js'
import { formatDay } from '../js/dates.js'

const SAVE_DELAY = 700

export async function renderQuestionnaire(root, { id, onBack }) {
  root.innerHTML = `<p class="lede">Opening…</p>`

  let data
  let answers = {}
  let error = null
  let saving = false
  let savedAt = null
  let saveTimer = null

  try {
    const result = await apiGet(`/questionnaires/${id}`)
    data = result.questionnaire
    answers = { ...data.answers }
  } catch (problem) {
    root.innerHTML = `
      <div>
        <button class="link-button" id="back">← Back</button>
        <section class="card">
          <p class="lede">${text(problem instanceof ApiError ? problem.message : 'This questionnaire is not available.')}</p>
        </section>
      </div>
    `
    root.querySelector('#back').addEventListener('click', onBack)

    return
  }

  function draw() {
    const answered = data.questions.filter((q) => answers[q.id] !== undefined && answers[q.id] !== '').length

    root.innerHTML = `
      <div class="questionnaire">
        <button class="link-button" id="back">← You</button>
        <h1 class="brief__title">${text(data.title)}</h1>

        ${
          data.submitted
            ? `<p class="success-line">${icon('check', { size: 20 })}<span>Submitted.</span></p>`
            : `<p class="lede">
                 Answer quickly and without polishing — the first answer is usually the true one.
               </p>
               <p class="questionnaire__progress">${answered} / ${data.questions.length}</p>`
        }

        <form id="form">
          ${data.questions.map(questionBlock).join('')}

          ${
            data.submitted
              ? ''
              : `<button class="button button--primary" type="submit" ${saving ? 'disabled' : ''}>
                   ${saving ? 'Wait…' : 'Submit'}
                 </button>`
          }
        </form>

        ${error ? `<p class="notice notice--error">${text(error)}</p>` : ''}
        ${savedAt && !data.submitted ? '<p class="journal__state">Saved.</p>' : ''}

        ${
          data.submitted && data.reveal_at && !data.result_ready
            ? `<p class="lede">The result opens ${formatDay(data.reveal_at)}.</p>`
            : ''
        }
      </div>
    `

    root.querySelector('#back').addEventListener('click', onBack)
    root.querySelector('#form').addEventListener('submit', submit)

    if (!data.submitted) {
      root.querySelectorAll('[data-question]').forEach((input) => {
        input.addEventListener('input', onInput)
        input.addEventListener('change', onInput)
      })
    }
  }

  function questionBlock(question) {
    const value = answers[question.id] ?? ''
    const disabled = data.submitted ? 'disabled' : ''

    if (question.type === 'scale') {
      const steps = []

      for (let step = question.min; step <= question.max; step += 1) {
        steps.push(`
          <label class="scale__step ${Number(value) === step ? 'scale__step--on' : ''}">
            <input type="radio" name="${question.id}" value="${step}" data-question="${question.id}"
                   class="visually-hidden" ${Number(value) === step ? 'checked' : ''} ${disabled} />
            <span>${step}</span>
          </label>
        `)
      }

      return `
        <fieldset class="question">
          <legend class="question__text">${text(question.text)}</legend>
          <div class="scale">${steps.join('')}</div>
          <p class="scale__labels">
            <span>${text(question.min_label)}</span><span>${text(question.max_label)}</span>
          </p>
        </fieldset>
      `
    }

    return `
      <label class="question">
        <span class="question__text">${text(question.text)}</span>
        <textarea class="field__input" rows="3" data-question="${question.id}"
                  ${disabled}>${text(value)}</textarea>
      </label>
    `
  }

  function onInput(event) {
    const id = event.target.dataset.question
    answers[id] = event.target.value

    // Repaint only the scale that changed; redrawing the form would steal focus.
    if (event.target.type === 'radio') {
      const group = event.target.closest('.scale')
      group.querySelectorAll('.scale__step').forEach((step) => {
        step.classList.toggle('scale__step--on', step.querySelector('input').checked)
      })
      updateProgress()
    }

    window.clearTimeout(saveTimer)
    saveTimer = window.setTimeout(save, SAVE_DELAY)
  }

  function updateProgress() {
    const answered = data.questions.filter((q) => answers[q.id] !== undefined && answers[q.id] !== '').length
    const element = root.querySelector('.questionnaire__progress')

    if (element) {
      element.textContent = `${answered} / ${data.questions.length}`
    }
  }

  async function save() {
    try {
      await apiPost(`/questionnaires/${data.id}/answers`, { answers, submit: false })
      savedAt = Date.now()

      const state = root.querySelector('.journal__state')
      if (state) {
        state.textContent = 'Saved.'
      } else {
        draw()
      }
    } catch {
      // A failed auto-save is not worth interrupting anybody over; the submit
      // sends everything again anyway.
    }
  }

  async function submit(event) {
    event.preventDefault()

    if (saving || data.submitted) return

    window.clearTimeout(saveTimer)
    saving = true
    error = null
    draw()

    try {
      const result = await apiPost(`/questionnaires/${data.id}/answers`, { answers, submit: true })
      data.submitted = true
      data.result_ready = result.result_ready
    } catch (problem) {
      error = problem instanceof ApiError ? problem.message : 'Predaja nije uspjela.'
    } finally {
      saving = false
      draw()
    }
  }

  draw()
}

// ---------------------------------------------------------------------------

export async function renderResult(root, { id, onBack }) {
  root.innerHTML = `<p class="lede">Opening…</p>`

  let result

  try {
    result = (await apiGet(`/questionnaires/${id}/result`)).result
  } catch (problem) {
    root.innerHTML = `
      <div>
        <button class="link-button" id="back">← You</button>
        <section class="card">
          <p class="lede">${text(problem instanceof ApiError ? problem.message : 'The result is not available.')}</p>
        </section>
      </div>
    `
    root.querySelector('#back').addEventListener('click', onBack)

    return
  }

  root.innerHTML = `
    <div class="result">
      <button class="link-button" id="back">← You</button>
      <h1 class="brief__title">${text(result.title)}</h1>
      <p class="lede">This is not a score. It is where you were when you answered.</p>

      ${result.dimensions
        .map(
          (dimension) => `
        <section class="dimension">
          <p class="dimension__label">${text(dimension.label)}</p>
          <p class="dimension__value">${text(dimension.value)}</p>
          <p class="dimension__text">${text(dimension.interpretation)}</p>
        </section>
      `,
        )
        .join('')}
    </div>
  `

  root.querySelector('#back').addEventListener('click', onBack)
}

